import type { DialogState, PatientInterviewState } from '../types';
import type { WebSocketCallbacks } from './interviewWebSocketTypes';

export interface InterviewWebSocketUiBridge {
  setConversations: WebSocketCallbacks['setConversations'];
  setCurrentInterimText: WebSocketCallbacks['setCurrentInterimText'];
  setFsmState: (state: DialogState) => void;
  setPatientState: (state: PatientInterviewState) => void;
  setTargetCategories: (categories: string[]) => void;
  setLastTiming: (timing: Record<string, number>) => void;
  setIsAwaitingResponse: (value: boolean) => void;
  setIsPlayingResponse: (value: boolean) => void;
  setSessionReady: (value: boolean) => void;
  setIsRecording: (value: boolean) => void;
  setCurrentVideoUrl: (value: string | null) => void;
  setRecordingTime: (value: number) => void;
  toast: WebSocketCallbacks['toast'];
}

/** React再描画後も、イベント実行時点の最新callbackへ転送するUI bridgeを作る。 */
export function createInterviewWebSocketUiBridge(
  getCallbacks: () => WebSocketCallbacks,
): InterviewWebSocketUiBridge {
  const shouldCollectDebugState = () => getCallbacks().collectDebugState === true;

  return {
    setConversations: (value) => getCallbacks().setConversations(value),
    setCurrentInterimText: (value) => getCallbacks().setCurrentInterimText(value),
    setFsmState: (value) => {
      if (shouldCollectDebugState()) getCallbacks().setFsmState(value);
    },
    setPatientState: (value) => {
      getCallbacks().setProgressPatientState?.(value);
      if (shouldCollectDebugState()) getCallbacks().setPatientState(value);
    },
    setTargetCategories: (value) => {
      if (shouldCollectDebugState()) getCallbacks().setTargetCategories(value);
    },
    setLastTiming: (value) => {
      if (shouldCollectDebugState()) getCallbacks().setLastTiming(value);
    },
    setIsAwaitingResponse: (value) => getCallbacks().setIsAwaitingResponse(value),
    setIsPlayingResponse: (value) => getCallbacks().setIsPlayingResponse(value),
    setSessionReady: (value) => getCallbacks().setIsSessionReady?.(value),
    setIsRecording: (value) => getCallbacks().setIsRecording(value),
    setCurrentVideoUrl: (value) => getCallbacks().setCurrentVideoUrl(value),
    setRecordingTime: (value) => getCallbacks().setRecordingTime(value),
    toast: (options) => getCallbacks().toast(options),
  };
}

/** 新しい面接WebSocketを開始する直前のUI・録音refを同じ初期状態へ戻す。 */
export function resetInterviewWebSocketUi(
  ui: InterviewWebSocketUiBridge,
  getCallbacks: () => WebSocketCallbacks,
): void {
  ui.setIsAwaitingResponse(false);
  ui.setIsPlayingResponse(false);
  ui.setIsRecording(false);
  ui.setCurrentInterimText('');
  getCallbacks().isRecordingRef.current = false;
  getCallbacks().isStoppingRef.current = false;
}

export interface MobileReconnectHookTarget {
  __mobileUiForceReconnect?: () => boolean;
}

export interface MobileReconnectHookInput {
  storage: Pick<Storage, 'getItem'>;
  target: object;
  forceReconnect: (reason: string) => boolean;
}

/** mobile UI状態テストが有効な時だけ再接続フックを公開し、cleanup関数を返す。 */
export function installMobileUiReconnectHook(input: MobileReconnectHookInput): () => void {
  try {
    if (input.storage.getItem('mobileUiStateTest') !== '1') return () => undefined;

    const target = input.target as MobileReconnectHookTarget;
    target.__mobileUiForceReconnect = () => input.forceReconnect('mobile-ui-state-test');
    return () => {
      delete target.__mobileUiForceReconnect;
    };
  } catch {
    // localStorageを利用できない環境ではテスト用フックを公開しない。
    return () => undefined;
  }
}
